import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import VerificationForm from "@/components/verifications/VerificationForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardCheck, CheckCircle2, XCircle, Clock, Minus, Pencil, Link as LinkIcon } from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

const TYPE_LABELS = {
  temperature_check: "Temperatures",
  cleaning_check: "Neteja",
  document_check: "Documentació",
  equipment_check: "Equips",
  goods_reception: "Recepció mercaderies",
  oil_control: "Olis",
  pest_control: "Plagues",
  preventive_maintenance: "Manteniment",
};

const FREQ_LABELS = {
  daily: "Diària", weekly: "Setmanal", monthly: "Mensual",
  quarterly: "Trimestral", annual: "Anual", on_demand: "A demanda",
};

const STATUS_CONFIG = {
  pending:        { label: "Pendent",     icon: Clock,         class: "bg-amber-50 text-amber-700 border-amber-200",  border: "border-l-amber-400" },
  correct:        { label: "Correcta",    icon: CheckCircle2,  class: "bg-emerald-50 text-emerald-700 border-emerald-200", border: "border-l-emerald-400" },
  incorrect:      { label: "Incorrecta",  icon: XCircle,       class: "bg-red-50 text-red-700 border-red-200",        border: "border-l-red-400" },
  not_applicable: { label: "No aplica",   icon: Minus,         class: "bg-gray-50 text-gray-500 border-gray-200",     border: "border-l-gray-300" },
};

export default function Verifications() {
  const { currentVenue, user, userRole, loading: venueLoading } = useVenue();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    if (currentVenue) load();
  }, [currentVenue]);

  const load = async () => {
    setLoading(true);
    const items = await hosys.entities.Verification.filter({ venue_id: currentVenue.id }, "-planned_date");
    setVerifications(items);
    setLoading(false);
  };

  const handleSave = async (data, shouldCreateIncident) => {
    let savedVerification;
    if (editing) {
      savedVerification = await hosys.entities.Verification.update(editing.id, data);
    } else {
      savedVerification = await hosys.entities.Verification.create(data);
    }

    if (shouldCreateIncident && data.status === "incorrect") {
      const incident = await hosys.entities.Incident.create({
        venue_id: currentVenue.id,
        title: `Verificació fallida: ${data.name}`,
        category: "other",
        description: data.observations || `Verificació de tipus "${TYPE_LABELS[data.type] || data.type}" ha resultat incorrecta.`,
        corrective_action: data.corrective_action || "",
        severity: "medium",
        status: "open",
        date: data.completed_date || new Date().toISOString().split("T")[0],
        responsible_name: data.responsible_name,
        responsible_email: data.responsible_email,
      });
      // Link incident back to verification
      const verificationId = editing ? editing.id : savedVerification?.id;
      if (verificationId) {
        await hosys.entities.Verification.update(verificationId, { linked_incident_id: incident.id });
      }
    }

    setEditing(null);
    await load();
  };

  if (venueLoading) return <QuickLoader />;

  const pendingCount = verifications.filter(v => v.status === "pending").length;
  const incorrectCount = verifications.filter(v => v.status === "incorrect").length;
  const correctCount = verifications.filter(v => v.status === "correct").length;
  const total = verifications.filter(v => v.status !== "pending" && v.status !== "not_applicable").length;
  const compliancePct = total > 0 ? Math.round((correctCount / total) * 100) : null;

  const filters = [
    { key: "all", label: `Totes (${verifications.length})` },
    { key: "pending", label: `Pendents (${pendingCount})` },
    { key: "incorrect", label: `Incorrectes (${incorrectCount})` },
    { key: "correct", label: `Correctes (${correctCount})` },
  ];

  const filtered = filter === "all" ? verifications : verifications.filter(v => v.status === filter);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Verificacions APPCC"
        subtitle={compliancePct !== null ? `${compliancePct}% de compliment` : "Gestió de controls periòdics"}
        actions={
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl">
            <Plus className="w-4 h-4" /> Nova verificació
          </Button>
        }
      />

      {/* Summary pills */}
      {verifications.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Pendents", count: pendingCount, color: "bg-amber-50 border-amber-200 text-amber-700" },
            { label: "Correctes", count: correctCount, color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
            { label: "Incorrectes", count: incorrectCount, color: "bg-red-50 border-red-200 text-red-700" },
            { label: "Compliment", count: compliancePct !== null ? `${compliancePct}%` : "—", color: "bg-blue-50 border-blue-200 text-blue-700" },
          ].map(({ label, count, color }) => (
            <div key={label} className={`rounded-xl border px-4 py-3 text-center ${color}`}>
              <p className="text-xl font-bold">{count}</p>
              <p className="text-xs font-medium">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter buttons */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {filters.map(({ key, label }) => (
          <Button
            key={key}
            variant={filter === key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(key)}
            className={`flex-shrink-0 ${filter === key ? "bg-[#7B2D3F] hover:bg-[#5B1D2F]" : ""}`}
          >
            {label}
          </Button>
        ))}
      </div>

      {loading ? (
        <QuickLoader />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ClipboardCheck}
          title="Sense verificacions"
          description="Crea verificacions periòdiques per controlar el compliment APPCC."
          action={
            <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
              <Plus className="w-4 h-4" /> Nova verificació
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((v) => {
            const sc = STATUS_CONFIG[v.status] || STATUS_CONFIG.pending;
            const StatusIcon = sc.icon;
            const isOverdue = v.status === "pending" && v.planned_date && moment(v.planned_date).isBefore(moment(), "day");
            return (
              <Card key={v.id} className={`p-4 border-0 shadow-sm hover:shadow-md transition-all border-l-4 ${sc.border}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${sc.class}`}>
                        <StatusIcon className="w-3 h-3" /> {sc.label}
                      </span>
                      <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                        {TYPE_LABELS[v.type] || v.type}
                      </span>
                      <span className="text-[11px] text-gray-400">{FREQ_LABELS[v.frequency]}</span>
                      {isOverdue && (
                        <span className="text-[11px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">Endarrerida</span>
                      )}
                      {v.linked_incident_id && (
                        <span className="inline-flex items-center gap-1 text-[11px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
                          <LinkIcon className="w-3 h-3" /> Incidència vinculada
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-0.5">{v.name}</h3>
                    {v.description && <p className="text-sm text-gray-500 mb-1 line-clamp-1">{v.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>📅 Prevista: {moment(v.planned_date).format("DD/MM/YYYY")}</span>
                      {v.completed_date && <span>✓ Realitzada: {moment(v.completed_date).format("DD/MM/YYYY")}</span>}
                      {v.responsible_name && <span>👤 {v.responsible_name}</span>}
                    </div>
                    {v.corrective_action && (
                      <div className="mt-2 p-2 bg-orange-50 rounded-lg">
                        <p className="text-xs font-semibold text-orange-700">Acció correctiva:</p>
                        <p className="text-xs text-orange-600">{v.corrective_action}</p>
                      </div>
                    )}
                    {v.observations && !v.corrective_action && (
                      <p className="text-xs text-gray-400 mt-1 italic">{v.observations}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setEditing(v); setShowForm(true); }}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-700"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <VerificationForm
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSave={handleSave}
        venueId={currentVenue?.id}
        user={user}
        editData={editing}
      />
    </div>
  );
}